import { MenuIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { Button, Checkbox, getSymbol, Label, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, SelectField } from "common";
import { FormikErrors, useFormikContext } from "formik";
import { produce } from "immer";
import { sortBy } from "lodash";
import React from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import {
    AddOn,
    AmountType,
    CatalogItem,
    CatalogItemObject,
    CatalogItemSequences,
    CatalogItemSequenceType,
    UnitType,
} from "types";
import { useSeller } from "../../../../hooks/useAccount";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { CurrencyInputField } from "../../../Field/CurrencyInputField";
import { AddOnEditorFormState, ChoicesItem } from "./AddOnTypes";
import { getResultingSequence, populateMissingSequences } from "@/models/CatalogItem";
import { reorder } from "@/utils/reorder";

interface FormikErrorsCatalogPriceItem {
    name?: string;
    price?: string;
}

export const DropdownItems = ({ disabled = false, addOn }: { disabled: boolean; addOn?: AddOn }) => {
    const { values, setFieldValue, errors } = useFormikContext<AddOnEditorFormState>();

    const seller = useSeller();
    const sellerCurrencySymbol = getSymbol(seller.currency);
    const isSequenceMissing = values.items.find((catalogItem) => !catalogItem.sequence);
    const catalogItems = isSequenceMissing ? populateMissingSequences(values.items) : values.items;

    const handleClick = () => {
        const lastSequence = catalogItems?.[catalogItems.length - 1]?.sequence;
        const items = produce(catalogItems, (draft) => {
            draft.push({
                id: `${catalogItems.length + 1}`,
                name: "",
                price: null,
                priceType: AmountType.ABSOLUTE,
                unitType: addOn?.unitType ?? UnitType.ADDON,
                sequence: (lastSequence ?? CatalogItemSequences.ADDON_SEQUENCE_START) + CatalogItemSequences.STEP,
            });
        });

        setFieldValue("items", items);
    };

    const handleDelete = (index: number) => {
        const items = produce(catalogItems, (draft) => {
            if ("id" in draft[index]) {
                draft[index]._remove = true;
            } else {
                draft.splice(index, 1);
            }
        });

        setFieldValue("items", items);
        const item = catalogItems[index];

        if ("id" in item && item.id === values.defaultChoice) {
            setFieldValue("defaultChoice", "");
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const resultingSequence = getResultingSequence({
            destinationIndex: result.destination.index,
            sourceIndex: result.source.index,
            productCatalogItems: catalogItems,
            type: CatalogItemSequenceType.ADDON,
        });

        const reOrderedItems = reorder(catalogItems, result.source.index, result.destination.index).map((item) => {
            if (item.id === result.draggableId) {
                return {
                    ...item,
                    sequence: resultingSequence,
                };
            }

            return item;
        });

        setFieldValue("items", reOrderedItems);
    };

    return (
        <div>
            <div
                className={clsx(
                    "mb-2 flex items-center",
                    addOn?.object !== CatalogItemObject.PRODUCT_CATALOG_ITEM && "ml-8",
                )}
            >
                <Label className="mr-2">Option</Label>
                <Label className="ml-40">Price</Label>
            </div>

            <fieldset>
                <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
                    <div>
                        <Droppable droppableId="addOnList">
                            {(provided: DroppableProvided) => (
                                <div
                                    ref={provided.innerRef}
                                    className="h-full flex-1 rounded"
                                    {...provided.droppableProps}
                                >
                                    <div className="divide-y-0 overflow-y-auto overflow-x-hidden rounded pr-1">
                                        {sortBy(values.items, "sequence").map((item, index) => {
                                            if (item._remove) {
                                                return null;
                                            }

                                            return (
                                                <DraggableItem
                                                    key={item.id}
                                                    item={item}
                                                    index={index}
                                                    errors={errors}
                                                    sellerCurrencySymbol={sellerCurrencySymbol}
                                                    values={values}
                                                    disabled={disabled}
                                                    setFieldValue={setFieldValue}
                                                    handleDelete={handleDelete}
                                                    addOn={addOn}
                                                />
                                            );
                                        })}
                                        <div>
                                            <SecondaryButton
                                                icon={<PlusIcon />}
                                                size="small"
                                                disabled={disabled}
                                                onClick={handleClick}
                                            >
                                                Add Option
                                            </SecondaryButton>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </div>
                </DragDropContext>
            </fieldset>
        </div>
    );
};

interface DraggableItemProps {
    item: CatalogItem | ChoicesItem;
    index: number;
    errors: FormikErrors<AddOnEditorFormState>;
    sellerCurrencySymbol: any;
    values: AddOnEditorFormState;
    disabled: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleDelete: (index: number) => void;
    addOn?: AddOn;
}

const DraggableItem = ({
    item,
    index,
    errors,
    sellerCurrencySymbol,
    values,
    disabled,
    setFieldValue,
    handleDelete,
    addOn,
}: DraggableItemProps) => {
    const handleCheck = (values: AddOnEditorFormState, item: CatalogItem | ChoicesItem) => {
        const isTheSameOption = values.defaultChoice === item.id;

        setFieldValue("defaultChoice", "id" in item && !isTheSameOption ? item.id : "");
    };

    const canDelete = values.items.filter((item) => !item._remove).length > 1;

    return (
        // Here the id field will definitely be present because it will either come from the request /api/catalogItems,
        // or we set it when creating new items in handleClick.
        <Draggable draggableId={item.id!} index={index}>
            {(provided) => {
                return (
                    <div
                        ref={provided.innerRef}
                        data-testid="dropdown-item"
                        {...provided.draggableProps}
                        // This solution was added because list items were positioned incorrectly when dragged by the user.
                        // We need to add property "auto" to fix the bug.
                        style={{ ...provided.draggableProps.style, left: "auto", top: "auto" }}
                    >
                        <div className="flex items-center space-x-4">
                            {addOn?.object !== CatalogItemObject.PRODUCT_CATALOG_ITEM && (
                                <div {...provided.dragHandleProps} className="mb-4">
                                    <MenuIcon size="small" />
                                </div>
                            )}

                            <div key={index} className="flex items-center space-x-2">
                                <FormField name={`items[${index}].name`}>
                                    <InputField isRequired className="h-10 min-w-40 flex-grow" disabled={disabled} />
                                </FormField>

                                <FormField name={`items[${index}].price`} className="flex-grow-2">
                                    <CurrencyInputField
                                        isRequired
                                        min="0"
                                        {...((item as ChoicesItem).priceType === AmountType.PERCENT
                                            ? { prefix: "%" }
                                            : {})}
                                        className={clsx(
                                            (errors?.items?.[index] as FormikErrorsCatalogPriceItem)?.price &&
                                                "border-danger",
                                            "w-full",
                                        )}
                                        disabled={false}
                                    />
                                </FormField>

                                <SelectField
                                    name={`items[${index}].priceType`}
                                    className={clsx("h-10", "-mt-4", "min-w-20")}
                                    disabled={false}
                                >
                                    <option className="no-translate" value={AmountType.ABSOLUTE}>
                                        {sellerCurrencySymbol}
                                    </option>
                                    <option value={AmountType.PERCENT}>% of purchasable items</option>
                                </SelectField>

                                {"id" in item && (
                                    <FormField name="defaultChoice" label={null}>
                                        <Checkbox
                                            label="Default"
                                            checked={"id" in item && values.defaultChoice === item.id}
                                            disabled={disabled}
                                            onChange={() => handleCheck(values, item)}
                                        />
                                    </FormField>
                                )}

                                <Tooltip content="At least one option is required" disabled={disabled || canDelete}>
                                    <Button
                                        variant="outline"
                                        color="secondary"
                                        size="medium"
                                        data-testid="dropdown-items-delete-button"
                                        className={clsx("-mt-4")}
                                        disabled={disabled || !canDelete}
                                        onClick={disabled ? undefined : () => handleDelete(index)}
                                    >
                                        <TrashIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                );
            }}
        </Draggable>
    );
};
